import { contentFingerprint, sha256Hex } from "./hash.js";
import { DepositError, type DepositLimits } from "./limits.js";
import { detectMediaType, extensionOf, isZip, sidecarType, UNSUPPORTED_IMAGE_EXTENSIONS, type ImageType, type StoredType } from "./media.js";
import { naturalCompare } from "./order.js";
import { readZip } from "./zip.js";

export type FileRole = "original_zip" | "slide" | "extra_image" | "sidecar";
export type FileOrigin = "uploaded_file" | "zip_entry";

/** One file of a deposit, with its exact bytes: never re-encoded. */
export interface PreparedFile {
  /** Stable within the preparation, used to reorder. */
  key: string;
  origin: FileOrigin;
  sourcePath: string;
  sha256: string;
  bytes: number;
  mediaType: StoredType;
  data: Uint8Array;
}

export interface PreparedDeposit {
  sourceKind: "zip" | "images";
  zip: PreparedFile | null;
  /** Images in slide order. */
  slides: PreparedFile[];
  /** Images kept but outside the carousel order. */
  extras: PreparedFile[];
  sidecars: PreparedFile[];
  /** What was not taken, with the reason (inside a ZIP, it stays in the original). */
  skipped: Array<{ path: string; reason: string }>;
  warnings: string[];
}

export interface InputFile {
  name: string;
  data: Uint8Array;
}

/** The manifest public.start_deposit receives (no bytes). */
export interface DepositManifest {
  source_kind: "zip" | "images";
  note: string | null;
  files: Array<{
    role: FileRole;
    origin: FileOrigin;
    source_path: string;
    position: number | null;
    sha256: string;
    bytes: number;
    media_type: StoredType;
  }>;
}

async function prepared(key: string, origin: FileOrigin, sourcePath: string, data: Uint8Array, mediaType: StoredType): Promise<PreparedFile> {
  return { key, origin, sourcePath, sha256: await sha256Hex(data), bytes: data.length, mediaType, data };
}

async function classify(
  items: Array<{ path: string; data: Uint8Array }>,
  origin: FileOrigin,
  limits: DepositLimits,
): Promise<Pick<PreparedDeposit, "slides" | "extras" | "sidecars" | "skipped" | "warnings">> {
  const images: PreparedFile[] = [];
  const sidecars: PreparedFile[] = [];
  const skipped: PreparedDeposit["skipped"] = [];
  const warnings: string[] = [];
  const sorted = [...items].sort((a, b) => naturalCompare(a.path, b.path));
  for (const [i, item] of sorted.entries()) {
    if (item.data.length > limits.maxFileBytes) {
      throw new DepositError("Fichier trop lourd.", [`${item.path} : ${Math.ceil(item.data.length / 1048576)} Mo (limite ${Math.floor(limits.maxFileBytes / 1048576)} Mo)`]);
    }
    const image = detectMediaType(item.data);
    if (image) {
      images.push(await prepared(`f${i}`, origin, item.path, item.data, image));
      continue;
    }
    const sidecar = sidecarType(item.path, item.data);
    if (sidecar) {
      sidecars.push(await prepared(`f${i}`, origin, item.path, item.data, sidecar));
      continue;
    }
    const ext = extensionOf(item.path);
    skipped.push({
      path: item.path,
      reason: UNSUPPORTED_IMAGE_EXTENSIONS.includes(ext) ? `format ${ext.toUpperCase()} non pris en charge (PNG, JPEG ou WebP)` : "type de fichier non utilisé",
    });
  }
  const seen = new Map<string, string>();
  for (const image of images) {
    const first = seen.get(image.sha256);
    if (first) warnings.push(`${image.sourcePath} est identique à ${first}`);
    else seen.set(image.sha256, image.sourcePath);
  }
  const slides = images.slice(0, limits.maxSlides);
  const extras = images.slice(limits.maxSlides);
  if (extras.length) warnings.push(`${images.length} images : les ${limits.maxSlides} premières forment le carrousel, les autres sont gardées hors ordre`);
  return { slides, extras, sidecars, skipped, warnings };
}

/** A ZIP: the original is kept as is, and its images are extracted byte for byte. */
export async function prepareFromZip(name: string, data: Uint8Array, limits: DepositLimits): Promise<PreparedDeposit> {
  if (!isZip(data)) throw new DepositError(`${name} n'est pas un ZIP.`);
  if (data.length > limits.maxFileBytes) {
    throw new DepositError("ZIP trop lourd.", [`${name} : ${Math.ceil(data.length / 1048576)} Mo (limite ${Math.floor(limits.maxFileBytes / 1048576)} Mo)`]);
  }
  const content = readZip(data, limits);
  const classified = await classify(content.entries, "zip_entry", limits);
  if (!classified.slides.length) throw new DepositError("Aucune image PNG, JPEG ou WebP dans ce ZIP.", classified.skipped.map((s) => `${s.path} : ${s.reason}`));
  return {
    sourceKind: "zip",
    zip: await prepared("zip", "uploaded_file", name, data, "application/zip"),
    ...classified,
    skipped: [...content.skipped, ...classified.skipped],
  };
}

/** Images (and descriptive files) dropped directly. */
export async function prepareFromImages(files: InputFile[], limits: DepositLimits): Promise<PreparedDeposit> {
  const classified = await classify(files.map((f) => ({ path: f.name, data: f.data })), "uploaded_file", limits);
  if (!classified.slides.length) throw new DepositError("Aucune image PNG, JPEG ou WebP.", classified.skipped.map((s) => `${s.path} : ${s.reason}`));
  return { sourceKind: "images", zip: null, ...classified };
}

/** One ZIP, or images: a mix is refused rather than guessed. */
export async function prepareDeposit(files: InputFile[], limits: DepositLimits): Promise<PreparedDeposit> {
  if (!files.length) throw new DepositError("Aucun fichier.");
  const zips = files.filter((f) => isZip(f.data));
  if (zips.length && files.length > 1) throw new DepositError("Un dépôt contient soit un ZIP, soit des images : pas les deux, ni plusieurs ZIP.");
  return zips.length ? prepareFromZip(zips[0]!.name, zips[0]!.data, limits) : prepareFromImages(files, limits);
}

/** New order: `slideKeys` in carousel order; every other image becomes an extra. */
export function withOrder(deposit: PreparedDeposit, slideKeys: string[], limits: DepositLimits): PreparedDeposit {
  const images = new Map([...deposit.slides, ...deposit.extras].map((f) => [f.key, f]));
  if (new Set(slideKeys).size !== slideKeys.length || slideKeys.some((k) => !images.has(k))) throw new DepositError("Ordre invalide.");
  if (!slideKeys.length) throw new DepositError("Le carrousel doit garder au moins une image.");
  if (slideKeys.length > limits.maxSlides) throw new DepositError(`Au plus ${limits.maxSlides} images dans le carrousel.`);
  const slides = slideKeys.map((k) => images.get(k)!);
  const extras = [...images.values()].filter((f) => !slideKeys.includes(f.key));
  return { ...deposit, slides, extras };
}

export function toManifest(deposit: PreparedDeposit, note: string | null, limits: DepositLimits): DepositManifest {
  const text = note?.trim() || null;
  if (text && text.length > limits.maxNoteChars) throw new DepositError(`Note trop longue (${limits.maxNoteChars} caractères au plus).`);
  const file = (f: PreparedFile, role: FileRole, position: number | null) => ({
    role,
    origin: f.origin,
    source_path: f.sourcePath.slice(0, 500),
    position,
    sha256: f.sha256,
    bytes: f.bytes,
    media_type: f.mediaType,
  });
  return {
    source_kind: deposit.sourceKind,
    note: text,
    files: [
      ...(deposit.zip ? [file(deposit.zip, "original_zip", null)] : []),
      ...deposit.slides.map((f, i) => file(f, "slide", i + 1)),
      ...deposit.extras.map((f) => file(f, "extra_image", null)),
      ...deposit.sidecars.map((f) => file(f, "sidecar", null)),
    ],
  };
}

export const imageHashes = (deposit: PreparedDeposit) => [...deposit.slides, ...deposit.extras].map((f) => f.sha256);
export const depositFingerprint = (deposit: PreparedDeposit) => contentFingerprint(imageHashes(deposit));

/** Every distinct object to store, by sha256, with its bytes and type. */
export function objectsOf(deposit: PreparedDeposit): Map<string, { data: Uint8Array; mediaType: StoredType }> {
  const all = [...(deposit.zip ? [deposit.zip] : []), ...deposit.slides, ...deposit.extras, ...deposit.sidecars];
  return new Map(all.map((f) => [f.sha256, { data: f.data, mediaType: f.mediaType }]));
}

export type { ImageType };
